import React, {Fragment, useEffect, useState} from "react";
import {Card, CardBody, Col, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table} from "reactstrap";
import Select from "react-select";
import {Container, Draggable} from "react-smooth-dnd";
import ReactPaginate from "react-paginate";
import CustomTagsInput from "../../../../../share-components/ui/customTagsInput";

/**
 * Shopify-style flexible variant editor: up to 3 options (drag to reorder - the
 * first option is the grouping option), values auto-sourced from the option
 * name (colour/size reuse the master lists, else free tags). The variants table
 * is grouped by the first option; each group header shows an image (pickable
 * from the product media) and opens a GTN / BIN modal for that group's variants.
 * Keeps `options` + `variants` + `variant_overrides` in sync.
 */
const MAX_OPTIONS = 3;
const TAG_ADD_KEYS = [13, 188]; // enter, comma (NOT space — values may contain spaces)

const detectSource = (name) => {
    const n = (name || "").trim().toLowerCase().replace(/s$/, "");
    if (n === "color" || n === "colour") return "colour";
    if (n === "size") return "size";
    return "free";
};

const comboKey = (values) => values.join(" || ");
const overrideKey = (values) => [...values].sort().join(" || ");
const genericSku = (code, values) => ((code || "") + "-" + values.join("-")).toUpperCase().replace(/\s+/g, "");
const normWidthToken = (w) => {
    const s = ("" + (w || "")).toUpperCase().replace(/\s+/g, "");
    return s && !/CM$/.test(s) && /\d/.test(s) ? s + "CM" : s;
};
const valueListFor = (option) => (option.values || []).map((v) => v.value);

const buildCombos = (opts) => {
    const lists = (opts || []).map(valueListFor).filter((l) => l.length > 0);
    if (lists.length === 0) return [];
    return lists.reduce((acc, list) => acc.flatMap((prefix) => list.map((v) => [...prefix, v])), [[]]);
};

const OptionsVariantEditor = (props) => {
    const {setValue, getValues, productState, errors, trigger, priceFlood} = props;
    const code = getValues("code");
    const productPrice = getValues("price");
    const productImages = (getValues("images") || []).filter((i) => i.media_type == 1);

    const [options, setOptions] = useState(() => getValues("options") || []);
    const [overrides, setOverrides] = useState(() => {
        const saved = getValues("variant_overrides") || {};
        const opts = getValues("options") || [];
        if (opts.length && Object.keys(saved).length) {
            const result = {...saved};
            buildCombos(opts).forEach((c) => {
                const k = overrideKey(c);
                if (!result[k]) result[k] = {deleted: true};
            });
            return result;
        }
        return saved;
    });
    const [priceFloodSeq, setPriceFloodSeq] = useState(0); // bump to remount price inputs after a flood
    const [committedModal, setCommittedModal] = useState(null); // {label, orders} -> committed-orders popup
    const [gtnBinGroup, setGtnBinGroup] = useState(null); // group value -> GTN/BIN modal open
    const [imageGroup, setImageGroup] = useState(null);   // group value -> image picker open
    const [imgPage, setImgPage] = useState(0);            // image picker pagination
    const [collapsed, setCollapsed] = useState({});       // group value -> collapsed
    // The ALL/hanger group image is kept here (ALL lives only in effectiveOptions,
    // not in the raw colour values) and re-injected on the ALL value.
    const [allGroupImageId, setAllGroupImageId] = useState(() => {
        const colOpt = (getValues("options") || []).find((o) => detectSource(o.name) === "colour");
        const allV = colOpt && (colOpt.values || []).find((v) => v.value === "ALL");
        return allV ? allV.image_id : null;
    });
    // colour_id -> image_id from the product as loaded, so a colour re-added after
    // removing the option keeps its group image (the backend also restores it).
    const [initialColourImages] = useState(() => {
        const m = {};
        (getValues("options") || []).forEach((o) => {
            if (detectSource(o.name) === "colour") {
                (o.values || []).forEach((v) => {
                    if (v.colour_id && v.image_id) m[v.colour_id] = v.image_id;
                });
            }
        });
        return m;
    });

    const toggleCollapse = (groupValue) =>
        setCollapsed((p) => ({...p, [groupValue]: !p[groupValue]}));

    // chunk product images into pages of 6 (same as the existing media modal)
    const imagePages = productImages.reduce((acc, item, i) => {
        const ci = Math.floor(i / 6);
        if (!acc[ci]) acc[ci] = [];
        acc[ci].push(item);
        return acc;
    }, []);

    // ---- fabric option roles (colour / width / size) + unit-type helpers ----
    const sizeMeta = productState.size_matrix || [];
    const hangerColour = productState.hanger_colour || null;
    // settings-driven sample/hanger price defaults + lock
    const fabricSamplePrice = productState.fabric_sample_price ?? 0.99;
    const fabricHangerPrice = productState.fabric_hanger_price ?? 10;
    const fabricPriceLocked = !!productState.fabric_price_locked;
    const unitTypeOf = (sizeMatrixId) => {
        const sm = sizeMeta.find((s) => s.id === sizeMatrixId);
        return sm ? sm.fabric_unit_type : null;
    };
    const roleOf = (o) => detectSource(o.name);
    const colourIdx = options.findIndex((o) => roleOf(o) === "colour");
    const sizeIdx = options.findIndex((o) => roleOf(o) === "size");
    const widthIdx = options.findIndex((o) => roleOf(o) === "free");
    const multiWidth = widthIdx >= 0 && (options[widthIdx].values || []).length > 1;
    const hasHangerUnit = sizeIdx >= 0 &&
        (options[sizeIdx].values || []).some((v) => unitTypeOf(v.size_matrix_id) === "hanger");

    // The hanger is one-per-product (the swatch card), so its variant uses the
    // special ALL colour. ALL is kept out of the colour picker and injected here
    // only while a hanger unit exists, so the user never picks it by hand.
    const effectiveOptions = options.map((o, i) => {
        if (i !== colourIdx) return o;
        const vals = (o.values || []).filter((v) => v.value !== "ALL");
        if (hasHangerUnit && hangerColour) {
            return {...o, values: [...vals, {value: "ALL", colour_id: hangerColour.id, image_id: allGroupImageId}]};
        }
        return {...o, values: vals};
    });

    const unitTypeForCombo = (c) => {
        if (sizeIdx < 0) return null;
        const vObj = (effectiveOptions[sizeIdx].values || []).find((v) => v.value === c[sizeIdx]);
        return vObj ? unitTypeOf(vObj.size_matrix_id) : null;
    };
    // hanger combos only on ALL; ALL only on hanger; everything else as-is.
    const isValidCombo = (c) => {
        const unit = unitTypeForCombo(c);
        const isAll = colourIdx >= 0 && c[colourIdx] === "ALL";
        if (unit === "hanger") return isAll;
        if (isAll) return false;
        return true;
    };

    const buildSku = (code, c) => {
        const unit = unitTypeForCombo(c);
        if (!unit) return genericSku(code, c);
        const colourVal = colourIdx >= 0 ? c[colourIdx] : "";
        const widthVal = widthIdx >= 0 ? c[widthIdx] : (getValues("fabric_width_cm") || "");
        const colourPart = unit === "hanger" ? "ALL" : colourVal;
        const w = normWidthToken(widthVal);
        let tail;
        if (unit === "per_metre") tail = w;
        else if (unit === "sample") tail = (multiWidth && w) ? w + "-SAMPLE" : "SAMPLE";
        else if (unit === "hanger") tail = (multiWidth && w) ? w + "-HANGER" : "HANGER";
        else tail = c[sizeIdx];
        return ((code || "") + "-" + colourPart + "-" + tail).toUpperCase().replace(/\s+/g, "").replace(/-+$/, "");
    };

    // sample/hanger prefill from settings; per-metre/other -> product price.
    const defaultPriceFor = (c) => {
        const u = unitTypeForCombo(c);
        if (u === "sample") return fabricSamplePrice;
        if (u === "hanger") return fabricHangerPrice;
        return productPrice;
    };
    const isLockedRow = (c) => {
        const u = unitTypeForCombo(c);
        return fabricPriceLocked && (u === "sample" || u === "hanger");
    };

    const combos = buildCombos(effectiveOptions).filter(isValidCombo);

    useEffect(() => {
        const variants = combos
            .filter((c) => !(overrides[overrideKey(c)] && overrides[overrideKey(c)].deleted))
            .map((c) => {
                const o = overrides[overrideKey(c)] || {};
                return {
                    option_values: c,
                    // auto SKUs are generated authoritatively by the backend; only
                    // send an SKU the user explicitly edited.
                    // only send a SKU the user explicitly edited this session;
                    // auto SKUs are (re)generated by the backend from the rule, so
                    // reloaded/stale auto SKUs self-correct instead of sticking.
                    sku: o.skuEdited && o.sku ? o.sku : "",
                    price: isLockedRow(c)
                        ? defaultPriceFor(c)
                        : (o.price !== undefined && o.price !== "" ? o.price : defaultPriceFor(c)),
                    cost_price: o.cost_price !== undefined ? o.cost_price : null,
                    qty: o.qty !== undefined && o.qty !== "" ? o.qty : 0,
                    gtn: o.gtn !== undefined ? o.gtn : "",
                    bins: o.bins || [],
                };
            });
        const withSource = effectiveOptions.map((o) => ({...o, value_source: detectSource(o.name)}));
        setValue("options", withSource);
        setValue("variants", variants);
        setValue("variant_overrides", overrides);
        // clear the "add at least one option" error live once an option exists
        if (options.length > 0 && trigger) trigger("options");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options, overrides, allGroupImageId]);

    // Flood every variant price when the product Price / Sale Price field changes
    // (mirrors the legacy grid). Sample & hanger keep their settings-driven price.
    useEffect(() => {
        if (!priceFlood || priceFlood.value === undefined || priceFlood.value === null || priceFlood.value === "") return;
        const val = priceFlood.value;
        setOverrides((prev) => {
            const next = {...prev};
            combos.forEach((c) => {
                const u = unitTypeForCombo(c);
                if (u === "sample" || u === "hanger") return;
                const k = overrideKey(c);
                next[k] = {...(next[k] || {}), price: val};
            });
            return next;
        });
        setPriceFloodSeq((s) => s + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [priceFlood]);

    // ---- options ----
    const updateOption = (index, patch) => setOptions((p) => p.map((o, i) => (i === index ? {...o, ...patch} : o)));
    const renameOption = (index, name) =>
        setOptions((p) => p.map((o, i) => {
            if (i !== index) return o;
            const next = {...o, name};
            if (detectSource(o.name) !== detectSource(name)) next.values = [];
            return next;
        }));
    const addOption = () => {
        if (options.length >= MAX_OPTIONS) return;
        setOptions((p) => [...p, {name: "", value_source: "free", position: p.length + 1, values: []}]);
    };
    const removeOption = (index) =>
        setOptions((p) => p.filter((_, i) => i !== index).map((o, i) => ({...o, position: i + 1})));
    const onOptionDrop = ({removedIndex, addedIndex}) => {
        if (removedIndex === null || addedIndex === null || removedIndex === addedIndex) return;
        setOptions((p) => {
            const next = [...p];
            const [moved] = next.splice(removedIndex, 1);
            next.splice(addedIndex, 0, moved);
            return next.map((o, i) => ({...o, position: i + 1}));
        });
    };
    const setColourValues = (index, sel) =>
        updateOption(index, {
            values: (sel || []).map((s) => {
                // keep an already-set image, else restore the colour's saved image.
                const existing = (options[index].values || []).find((v) => v.colour_id === s.id);
                const imageId = existing && existing.image_id !== undefined && existing.image_id !== null
                    ? existing.image_id
                    : initialColourImages[s.id];
                return {value: s.name, colour_id: s.id, image_id: imageId};
            }),
        });
    const setSizeValues = (index, sel) =>
        updateOption(index, {values: (sel || []).map((s) => ({value: s.name, size_matrix_id: s.id}))});
    const setFreeValues = (index, tags) =>
        updateOption(index, {values: (tags || []).map((v) => ({value: v}))});

    const patchOverride = (key, patch) => setOverrides((p) => ({...p, [key]: {...(p[key] || {}), ...patch}}));

    // ---- group image (stored on the first option's value.image_id) ----
    const setGroupImage = (groupValue, imageId) => {
        if (groupValue === "ALL") {
            setAllGroupImageId(imageId);
            return;
        }
        setOptions((p) => p.map((o, i) =>
            i === 0 ? {...o, values: (o.values || []).map((v) => (v.value === groupValue ? {...v, image_id: imageId} : v))} : o));
    };
    const groupImageId = (groupValue) => {
        if (groupValue === "ALL") return allGroupImageId;
        const v = (options[0] && options[0].values || []).find((x) => x.value === groupValue);
        return v ? v.image_id : null;
    };
    const imagePathById = (id) => {
        const img = productImages.find((i) => i.id === id);
        return img ? img.image_path : null;
    };
    const openImagePicker = (groupValue) => {
        const selId = groupImageId(groupValue);
        let page = 0;
        imagePages.forEach((chunk, i) => {
            if (chunk.some((img) => img.id === selId)) page = i;
        });
        setImgPage(page);
        setImageGroup(groupValue);
    };

    const colourOptions = productState.colors || [];
    const sizeOptions = productState.size_matrix || [];

    const renderValueInput = (option, index, source) => {
        if (source === "colour") {
            return (
                <Select isMulti getOptionLabel={(o) => o.name} getOptionValue={(o) => o.id}
                        menuPortalTarget={document.body} styles={{menuPortal: (b) => ({...b, zIndex: 100})}}
                        value={(option.values || []).map((v) => colourOptions.find((c) => c.id === v.colour_id)).filter(Boolean)}
                        options={colourOptions} onChange={(s) => setColourValues(index, s)}/>
            );
        }
        if (source === "size") {
            return (
                <Select isMulti getOptionLabel={(o) => o.name} getOptionValue={(o) => o.id}
                        menuPortalTarget={document.body} styles={{menuPortal: (b) => ({...b, zIndex: 100})}}
                        value={(option.values || []).map((v) => sizeOptions.find((s) => s.id === v.size_matrix_id)).filter(Boolean)}
                        options={sizeOptions} onChange={(s) => setSizeValues(index, s)}/>
            );
        }
        return (
            <CustomTagsInput value={(option.values || []).map((v) => v.value)} addKeys={TAG_ADD_KEYS}
                             inputPlaceholder="Type a value (comma / enter)"
                             tagChangeEvent={(tags) => setFreeValues(index, tags)}/>
        );
    };

    // ---- variants grouped by the first option ----
    const groups = [];
    const groupIndex = {};
    combos.forEach((c) => {
        const g = c[0];
        if (groupIndex[g] === undefined) {
            groupIndex[g] = groups.length;
            groups.push({value: g, rows: []});
        }
        groups[groupIndex[g]].rows.push(c);
    });

    const groupCombos = (groupValue) => combos.filter((c) => c[0] === groupValue);

    // "complete" when every active variant in the group has a GTN and a bin.
    const groupComplete = (groupValue) => {
        const rows = groupCombos(groupValue).filter((c) => !(overrides[overrideKey(c)] && overrides[overrideKey(c)].deleted));
        if (rows.length === 0) return true;
        return rows.every((c) => {
            const o = overrides[overrideKey(c)] || {};
            return o.gtn && o.gtn !== "" && o.bins && o.bins.length > 0;
        });
    };

    const variantRow = (c) => {
        const key = overrideKey(c);
        const o = overrides[key] || {};
        const subLabel = c.length > 1 ? c.slice(1).join(" / ") : c[0];
        if (o.deleted) {
            return (
                <tr key={comboKey(c)} className="text-muted">
                    <td className="pl-4">{subLabel}</td>
                    <td colSpan="3"><em>removed</em></td>
                    <td><button type="button" className="btn btn-link btn-sm p-0"
                                onClick={() => patchOverride(key, {deleted: false})}>undo</button></td>
                </tr>
            );
        }
        const locked = isLockedRow(c);
        return (
            <tr key={comboKey(c)}>
                <td className="pl-4">
                    {subLabel}
                    {o.committed > 0 &&
                        <button type="button" className="ml-2" title="Committed in orders — click to view"
                                style={{
                                    cursor: "pointer", border: "none", textDecoration: "none",
                                    background: "#ffc107", color: "#212529", borderRadius: 12,
                                    padding: "1px 9px", fontSize: 12, fontWeight: 700, lineHeight: 1.5
                                }}
                                onClick={() => setCommittedModal({
                                    label: subLabel,
                                    sku: o.skuEdited && o.sku ? o.sku : buildSku(code, c),
                                    orders: o.committed_orders || []
                                })}>
                            {o.committed} committed
                        </button>}
                </td>
                <td><Input bsSize="sm" type="text"
                           defaultValue={o.skuEdited && o.sku ? o.sku : buildSku(code, c)}
                           onBlur={(e) => patchOverride(key, {sku: e.target.value, skuEdited: true})}/></td>
                <td><Input key={"p-" + key + "-" + priceFloodSeq} bsSize="sm" type="number" step="any" disabled={locked}
                           title={locked ? "Locked to the fabric price setting" : undefined}
                           defaultValue={locked ? defaultPriceFor(c) : (o.price !== undefined && o.price !== "" ? o.price : defaultPriceFor(c))}
                           onBlur={(e) => patchOverride(key, {price: e.target.value})}/></td>
                <td><Input bsSize="sm" type="number"
                           defaultValue={o.qty !== undefined && o.qty !== "" ? o.qty : 0}
                           onBlur={(e) => patchOverride(key, {qty: e.target.value})}/></td>
                <td><button type="button" className="btn btn-outline-danger btn-sm"
                            onClick={() => patchOverride(key, {deleted: true})}>x</button></td>
            </tr>
        );
    };

    return (
        <Fragment>
            <Card>
                <CardBody id="variant-options-anchor">
                    <div className="ribbon ribbon-clip ribbon-primary ribbon-left mb-5">Variant Options</div>
                    {errors && errors.options &&
                        <div className="validation-msg mt-4 mb-2">{errors.options.message}</div>}

                    {options.length > 0 && (
                        <div className="mt-3">
                            <Container dragHandleSelector=".option-drag-handle" lockAxis="y" onDrop={onOptionDrop}>
                                {options.map((option, index) => {
                                    const source = detectSource(option.name);
                                    return (
                                        <Draggable key={index}>
                                            <Row className="align-items-end mb-3 pb-3 mx-0"
                                                 style={{borderBottom: "1px solid #f0f0f0"}}>
                                                <Col xs="auto" className="d-flex align-items-center px-1"
                                                     style={{paddingBottom: 8}}>
                                                    <span className="option-drag-handle" title="Drag to reorder"
                                                          style={{cursor: "grab", color: "#9aa0a6", lineHeight: 1}}>
                                                        <i className="fa fa-ellipsis-v"/>
                                                        <i className="fa fa-ellipsis-v" style={{marginLeft: 2}}/>
                                                    </span>
                                                </Col>
                                                <Col md="3">
                                                    <FormGroup className="mb-0">
                                                        <Label className="col-form-label">Option Name</Label>
                                                        <Input type="text" value={option.name}
                                                               placeholder="e.g. Color, Width, Size"
                                                               onChange={(e) => renameOption(index, e.target.value)}/>
                                                    </FormGroup>
                                                </Col>
                                                <Col md="6">
                                                    <FormGroup className="mb-0">
                                                        <Label className="col-form-label">
                                                            Values {index === 0 ?
                                                            <span className="text-primary">(grouped by this)</span> : ""}
                                                        </Label>
                                                        {renderValueInput(option, index, source)}
                                                    </FormGroup>
                                                </Col>
                                                <Col md="2">
                                                    <button type="button" className="btn btn-outline-danger"
                                                            onClick={() => removeOption(index)}>Remove</button>
                                                </Col>
                                            </Row>
                                        </Draggable>
                                    );
                                })}
                            </Container>
                        </div>
                    )}

                    {options.length === 0 && (
                        <p className="text-muted mt-3 mb-2">
                            No options yet. Add options like <strong>Size</strong>, <strong>Colour</strong> or
                            {" "}<strong>Width</strong> to generate your variants.
                        </p>
                    )}
                    {options.length < MAX_OPTIONS && (
                        <div className={options.length === 0 ? "mb-2" : "mt-3"}>
                            <button type="button" className="btn btn-outline-primary btn-sm" onClick={addOption}>
                                + Add Option
                            </button>
                        </div>
                    )}
                </CardBody>
            </Card>

            <Card>
                <CardBody>
                    <div className="ribbon ribbon-clip ribbon-success ribbon-left mb-5">Variants</div>
                    {combos.length === 0 ? (
                        <p className="text-muted mt-4">Add options and values above to generate variants.</p>
                    ) : (
                        <Table responsive bordered size="sm" className="mt-2">
                            <thead>
                            <tr>
                                <th style={{width: "26%"}}>Variant</th>
                                <th style={{width: "36%"}}>SKU</th>
                                <th style={{width: "14%"}}>Price</th>
                                <th style={{width: "14%"}}>Qty</th>
                                <th style={{width: "10%"}}>Remove</th>
                            </tr>
                            </thead>
                            <tbody>
                            {groups.map((group) => {
                                const imgId = groupImageId(group.value);
                                const imgPath = imgId ? imagePathById(imgId) : null;
                                const isCollapsed = !!collapsed[group.value];
                                const complete = groupComplete(group.value);
                                return (
                                    <Fragment key={group.value}>
                                        <tr className="table-light">
                                            <td colSpan="5">
                                                <div className="d-flex align-items-center">
                                                    <span style={{cursor: "pointer", color: "#666", marginLeft: -6, marginRight: 14}}
                                                          title={isCollapsed ? "Expand" : "Collapse"}
                                                          onClick={() => toggleCollapse(group.value)}>
                                                        <i className={isCollapsed ? "fa fa-chevron-right" : "fa fa-chevron-down"}/>
                                                    </span>
                                                    <div onClick={() => openImagePicker(group.value)} title="Set image"
                                                         style={{
                                                             width: 34, height: 34, marginRight: 10, cursor: "pointer",
                                                             borderRadius: 4, border: "1px solid #ddd", overflow: "hidden",
                                                             display: "flex", alignItems: "center", justifyContent: "center",
                                                             background: "#f6f6f6", color: "#bbb", flex: "0 0 auto"
                                                         }}>
                                                        {imgPath
                                                            ? <img src={imgPath} alt="" style={{width: "100%", height: "100%", objectFit: "cover"}}/>
                                                            : <i className="fa fa-image"/>}
                                                    </div>
                                                    <strong className="mr-2" style={{cursor: "pointer"}}
                                                            title="Edit GTN / BIN" onClick={() => setGtnBinGroup(group.value)}>
                                                        {group.value}
                                                    </strong>
                                                    {complete
                                                        ? <i className="fa fa-check-circle text-success" title="GTN & BIN complete"/>
                                                        : <i className="fa fa-exclamation-circle text-warning" title="GTN / BIN not fully updated"/>}
                                                </div>
                                            </td>
                                        </tr>
                                        {!isCollapsed && group.rows.map((c) => variantRow(c))}
                                    </Fragment>
                                );
                            })}
                            </tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>

            {/* ---- GTN / BIN modal (per group) — same layout as the colour SKU modal ---- */}
            <Modal size="lg" className="options-wide-modal" isOpen={gtnBinGroup !== null} toggle={() => setGtnBinGroup(null)}>
                <ModalHeader toggle={() => setGtnBinGroup(null)}>{gtnBinGroup + "'s sku"}</ModalHeader>
                <ModalBody>
                    <Row className="product-name mb-3">
                        <Col md={4} className="text-center"><strong>SKU</strong></Col>
                        <Col md={4} className="text-center"><strong>GTN Number</strong></Col>
                        <Col md={4} className="text-center"><strong>BIN Number</strong></Col>
                    </Row>
                    {gtnBinGroup !== null && groupCombos(gtnBinGroup).map((c) => {
                        const key = overrideKey(c);
                        const o = overrides[key] || {};
                        if (o.deleted) return null;
                        const sku = o.skuEdited && o.sku ? o.sku : buildSku(code, c);
                        return (
                            <Row key={comboKey(c)} className="mb-1">
                                <Col sm={12}>
                                    <div style={{marginBottom: 10}}>
                                        <div className="media">
                                            <div className="media-body ml-3">
                                                <Row className="product-name">
                                                    <Col md={4}>
                                                        <h6 className="mt-2" style={{fontSize: 14}}>{sku.toUpperCase()}</h6>
                                                    </Col>
                                                    <Col md={4}>
                                                        <input name="gtn" type="text" defaultValue={o.gtn || ""}
                                                               placeholder="GTIN #" className="form-control"
                                                               onChange={(e) => patchOverride(key, {gtn: e.target.value})}/>
                                                    </Col>
                                                    <Col md={4}>
                                                        <CustomTagsInput value={o.bins || []} addKeys={TAG_ADD_KEYS}
                                                                         tagChangeEvent={(tags) => patchOverride(key, {bins: tags})}/>
                                                    </Col>
                                                </Row>
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        );
                    })}
                </ModalBody>
                <ModalFooter>
                    <Row>
                        <Col md={12}>
                            <button type="button" className="btn btn-primary"
                                    onClick={() => setGtnBinGroup(null)}>Save
                            </button>
                        </Col>
                    </Row>
                </ModalFooter>
                <Row className="mb-3 mt-3">
                    <Col md={12}>
                        <div className="text-center">
                            <span>{"Temporary save only! For permanent save, use the Publish or save button."}</span>
                        </div>
                    </Col>
                </Row>
            </Modal>

            {/* ---- image picker (per group) — same layout as the media modal ---- */}
            <Modal size="lg" isOpen={imageGroup !== null} toggle={() => setImageGroup(null)}>
                <ModalHeader toggle={() => setImageGroup(null)}>{imageGroup}</ModalHeader>
                <ModalBody>
                    {productImages.length === 0 ? (
                        <p className="text-muted">No images uploaded yet. Add product images in the Media section first.</p>
                    ) : (
                        <Fragment>
                            <div className="form-row mb-3 options-image-picker">
                                {imagePages.length > 0 && (
                                    <div style={{display: "contents"}}>
                                        {(imagePages[imgPage] || []).map((item, i) => (
                                            <Col md={4} key={i} data-id={item.id}>
                                                <Card>
                                                    <div className="product-box variant-image"
                                                         onClick={() => {
                                                             setGroupImage(imageGroup, item.id);
                                                             setImageGroup(null);
                                                         }}>
                                                        <div className="product-img">
                                                            <div className={`image-crop-center ${groupImageId(imageGroup) === item.id ? "active-variant-image" : ""}`}
                                                                 style={{height: 200}}>
                                                                <img className="img-fluid scale-image-6" src={item.image_path} alt=""/>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </Card>
                                            </Col>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <Row>
                                <Col md={12}>
                                    <div style={{display: "inline-flex", float: "right"}}>
                                        <div>
                                            <ReactPaginate
                                                className="pagination"
                                                activeClassName="pagination-active"
                                                breakLabel="..."
                                                nextLabel="next >"
                                                onPageChange={(event) => setImgPage(event.selected)}
                                                pageRangeDisplayed={5}
                                                forcePage={imgPage}
                                                pageCount={imagePages.length}
                                                previousLabel="< previous"
                                                renderOnZeroPageCount={null}
                                            />
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Fragment>
                    )}
                </ModalBody>
            </Modal>

            {/* ---- committed orders popup (which orders hold this variant's committed qty) ---- */}
            <Modal isOpen={committedModal !== null} toggle={() => setCommittedModal(null)}>
                <ModalHeader toggle={() => setCommittedModal(null)}>
                    Committed orders{committedModal ? " — " + committedModal.label : ""}
                </ModalHeader>
                <ModalBody>
                    {committedModal && committedModal.sku &&
                        <div className="mb-3"><strong>SKU:</strong> {committedModal.sku}</div>}
                    {committedModal && committedModal.orders.length > 0 ? (
                        <Table size="sm" className="mb-0">
                            <thead>
                            <tr>
                                <th>Order</th>
                                <th className="text-right">Quantity</th>
                            </tr>
                            </thead>
                            <tbody>
                            {committedModal.orders.map((ord, idx) => (
                                <tr key={ord.order_id || idx}>
                                    <td>
                                        {ord.order_id
                                            ? <a href={`${process.env.PUBLIC_URL}/order/${ord.order_id}/view`}
                                                 target="_blank" rel="noopener noreferrer">{ord.order_number}</a>
                                            : ord.order_number}
                                    </td>
                                    <td className="text-right">{ord.quantity}</td>
                                </tr>
                            ))}
                            </tbody>
                        </Table>
                    ) : (
                        <p className="text-muted mb-0">No committed orders.</p>
                    )}
                </ModalBody>
            </Modal>
        </Fragment>
    );
};

export default OptionsVariantEditor;
